import { Drawer } from "vaul";
import { Link, useRouterState } from "@tanstack/react-router";
import {
	BarChart3,
	User,
	Users,
	Globe,
	FileText,
	Settings,
	MessageSquare,
	LogOut,
	Sun,
	Moon,
	Monitor,
} from "lucide-react";
import { useAuth } from "../../lib/auth-context";
import { useTheme, type Theme } from "../../lib/theme-context";

type MoreSheetProps = {
	open: boolean;
	onClose: () => void;
};

const themeIcons: Record<Theme, typeof Sun> = {
	light: Sun,
	dark: Moon,
	system: Monitor,
};

const themeLabels: Record<Theme, string> = {
	light: "Light",
	dark: "Dark",
	system: "System",
};

const themeCycle: Record<Theme, Theme> = {
	light: "dark",
	dark: "system",
	system: "light",
};

const MORE_ITEMS = [
	{ to: "/analytics", icon: BarChart3, label: "Analytics" },
	{ to: "/profile", icon: User, label: "Profile" },
	{ to: "/team", icon: Users, label: "Team" },
	{ to: "/website", icon: Globe, label: "Website" },
	{ to: "/blogs", icon: FileText, label: "Blog" },
	{ to: "/settings", icon: Settings, label: "Settings" },
] as const;

export function MoreSheet({ open, onClose }: MoreSheetProps) {
	const routerState = useRouterState();
	const { signOut } = useAuth();
	const { theme, setTheme } = useTheme();
	const ThemeIcon = themeIcons[theme];

	return (
		<Drawer.Root
			open={open}
			onOpenChange={(o) => {
				if (!o) onClose();
			}}
		>
			<Drawer.Portal>
				<Drawer.Overlay className="fixed inset-0 z-[55] bg-black/50" />
				<Drawer.Content className="fixed inset-x-0 bottom-0 z-[55] rounded-t-2xl bg-background-elevated pb-safe">
					<div className="mx-auto mt-3 mb-2 h-1 w-10 flex-shrink-0 rounded-full bg-border-default" />
					<Drawer.Title className="sr-only">More options</Drawer.Title>
					<nav className="px-2 pb-4">
						<ul className="grid grid-cols-3 gap-1">
							{MORE_ITEMS.map(({ to, icon: Icon, label }) => {
								const isActive =
									routerState.location.pathname.startsWith(to);
								return (
									<li key={to}>
										<Link
											to={to}
											onClick={onClose}
											className={`flex flex-col items-center gap-1.5 rounded-xl px-3 py-3 text-xs transition-colors ${
												isActive
													? "bg-primary-100 text-primary-700"
													: "text-foreground-muted hover:bg-background-muted"
											}`}
										>
											<Icon className="h-5 w-5" />
											{label}
										</Link>
									</li>
								);
							})}
						</ul>
						<div className="mt-3 border-t border-border-default pt-3 px-1 flex gap-1">
							<button
								type="button"
								onClick={() => {
									onClose();
									window.dispatchEvent(new CustomEvent("open-feedback"));
								}}
								className="flex flex-1 flex-col items-center gap-1.5 rounded-xl px-2 py-3 text-xs text-foreground-muted hover:bg-background-muted transition-colors"
							>
								<MessageSquare className="h-5 w-5" />
								Feedback
							</button>
							<button
								type="button"
								onClick={() => setTheme(themeCycle[theme])}
								className="flex flex-1 flex-col items-center gap-1.5 rounded-xl px-2 py-3 text-xs text-foreground-muted hover:bg-background-muted transition-colors"
							>
								<ThemeIcon className="h-5 w-5" />
								{themeLabels[theme]}
							</button>
							<button
								type="button"
								onClick={() => {
									onClose();
									signOut();
								}}
								className="flex flex-1 flex-col items-center gap-1.5 rounded-xl px-2 py-3 text-xs text-error hover:bg-error/10 transition-colors"
							>
								<LogOut className="h-5 w-5" />
								Logout
							</button>
						</div>
					</nav>
				</Drawer.Content>
			</Drawer.Portal>
		</Drawer.Root>
	);
}
